import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingSubscriptionPlans, setLoadingSubscriptionPlans] = useState(false);
  const [subscriptionPlansError, setSubscriptionPlansError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [formData, setFormData] = useState({
    businessName: '',
    businessEmail: '',
    businessPhone: '',
    EmergencyMobNo: '',
    password: '',
    businessSubscriptionPlan: '',
    businessCreatedDate: '',
    weeklyHoliday: [],
    address: '',
    businessLogo: null,
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const validateToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      return null;
    }
    
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setError('Session expired. Please log in again.');
        return null;
      }
      return token;
    } catch (error) {
      console.error('Invalid token:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('userRole');
      setError('Invalid session. Please log in again.');
      return null;
    }
  };

  const fetchSubscriptionPlans = async () => {
    const token = validateToken();
    if (!token) {
      setSubscriptionPlansError('No access token found');
      return;
    }

    setLoadingSubscriptionPlans(true);
    setSubscriptionPlansError(null);

    try {
      console.log('🚀 FETCHING SUBSCRIPTION PLANS FOR COMPANIES');
      console.log('├── Token available:', !!token);
      
      // First, try to get company/user ID from JWT token
      const decoded = jwtDecode(token);
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
      
      console.log('├── Decoded token for subscription plans:', {
        companyId,
        fullDecoded: decoded
      });

      // Try different API endpoints for subscription plans
      let response;
      let apiEndpoint;
      
      if (companyId) {
        // Try company-specific subscription plans first
        apiEndpoint = `https://customersupport-jhy9.onrender.com/api/subscription-plans/${companyId}`;
        console.log('├── Trying company-specific endpoint:', apiEndpoint);
        
        try {
          response = await axios.get(apiEndpoint, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status < 500
          });
        } catch (err) {
          console.log('├── Company-specific endpoint failed, trying general endpoint');
        }
      }
      
      // If company-specific failed or no companyId, try general endpoint
      if (!response || response.status >= 400) {
        apiEndpoint = 'https://customersupport-jhy9.onrender.com/api/subscription-plans';
        console.log('├── Trying general endpoint:', apiEndpoint);
        
        response = await axios.get(apiEndpoint, {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        });
      }

      console.log('📡 SUBSCRIPTION PLANS API RESPONSE:');
      console.log('├── Status:', response.status);
      console.log('├── Headers:', response.headers);
      console.log('├── Data:', response.data);

      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setSubscriptionPlansError('Session expired. Please log in again.');
        return;
      }

      if (response.status === 404) {
        console.log('⚠️ No subscription plans found');
        setSubscriptionPlans([]);
        setSubscriptionPlansError('No subscription plans available');
        return;
      }

      if (response.status >= 400) {
        console.error('❌ Error response:', response.data);
        setSubscriptionPlansError(`Error fetching subscription plans: ${response.data?.message || 'Server error'}`);
        return;
      }

      const planData = response.data;
      let processedPlans = [];

      // Handle different response formats
      if (Array.isArray(planData)) {
        processedPlans = planData;
      } else if (planData && Array.isArray(planData.subscriptionPlans)) {
        processedPlans = planData.subscriptionPlans;
      } else if (planData && Array.isArray(planData.data)) {
        processedPlans = planData.data;
      } else if (planData && Array.isArray(planData.plans)) {
        processedPlans = planData.plans;
      } else if (planData && typeof planData === 'object') {
        // Single plan object
        processedPlans = [planData];
      } else {
        console.log('⚠️ Unexpected response format:', planData);
        processedPlans = [];
      }

      console.log('🎯 PROCESSED SUBSCRIPTION PLANS:');
      console.log('├── Total plans found:', processedPlans.length);
      
      processedPlans.forEach((plan, index) => {
        console.log(`├── Plan ${index + 1}:`);
        console.log(`    ├── ID: ${plan._id || plan.id || 'N/A'}`);
        console.log(`    ├── Title/Name: ${plan.title || plan.name || plan.planName || 'N/A'}`);
        console.log(`    ├── Description: ${plan.description || 'N/A'}`);
        console.log(`    ├── Price: ${plan.price || plan.cost || plan.amount || 'N/A'}`);
        console.log(`    ├── Currency: ${plan.currency || 'INR'}`);
        console.log(`    ├── Duration: ${plan.duration || plan.period || plan.billingCycle || 'N/A'}`);
        console.log(`    ├── Features: ${plan.features ? plan.features.length : 'N/A'}`);
        console.log(`    ├── Active: ${plan.isActive !== undefined ? plan.isActive : 'N/A'}`);
        console.log(`    ├── Display Text: "${(plan.title || plan.name || plan.planName || 'Unknown Plan')} - ${plan.currency || '₹'}${plan.price || plan.cost || plan.amount || '0'} (${plan.duration || plan.period || plan.billingCycle || 'N/A'})"`);        console.log(`    └── Company: ${plan.company || plan.companyId || 'N/A'}`);
      });

      setSubscriptionPlans(processedPlans);
      
      if (processedPlans.length === 0) {
        setSubscriptionPlansError('No subscription plans available');
      } else {
        console.log(`✅ Successfully loaded ${processedPlans.length} subscription plans`);
      }

    } catch (err) {
      console.error('❌ Error fetching subscription plans:', err);
      
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        setSubscriptionPlansError('Session expired. Please log in again.');
      } else if (err.response?.status === 404) {
        setSubscriptionPlansError('Subscription plans service not found');
      } else if (err.response?.status >= 500) {
        setSubscriptionPlansError('Server error. Please try again later.');
      } else {
        setSubscriptionPlansError(`Network error: ${err.message}`);
      }
    } finally {
      setLoadingSubscriptionPlans(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchSubscriptionPlans();
  }, []);

  const fetchCompanies = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('https://customersupport-jhy9.onrender.com/api/companies', {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Fetched companies:", response.data);
      setCompanies(response.data);
    } catch (err) {
      console.error("Error fetching companies:", err.response || err.message || err);
      setError('Failed to fetch companies. You might not be authorized.');
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file, maxWidth = 300, maxHeight = 300, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleInputChange = async (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert('File size must be less than 5MB');
          e.target.value = '';
          return;
        }
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
          alert('Please select a valid image file');
          e.target.value = '';
          return;
        }
        
        try {
          // Compress the image
          const compressedFile = await compressImage(file);
          
          setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: compressedFile,
          }));
          
          // Create preview URL for logo
          const reader = new FileReader();
          reader.onloadend = () => {
            setLogoPreview(reader.result);
          };
          reader.readAsDataURL(compressedFile);
        } catch (error) {
          console.error('Error compressing image:', error);
          alert('Error processing image. Please try another file.');
        }
      }
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const handleWeeklyHolidayChange = (day) => {
    setFormData((prevFormData) => {
      const currentHolidays = prevFormData.weeklyHoliday;
      const updatedHolidays = currentHolidays.includes(day)
        ? currentHolidays.filter(d => d !== day)
        : [...currentHolidays, day];
      
      return {
        ...prevFormData,
        weeklyHoliday: updatedHolidays,
      };
    });
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    try {
      // If there's a logo file, convert it to base64
      let logoBase64 = null;
      if (formData.businessLogo) {
        const reader = new FileReader();
        logoBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            const result = reader.result;
            // Check if base64 string is too large (limit to ~500KB after encoding)
            if (result.length > 700000) {
              alert('Image is still too large after compression. Please choose a smaller image.');
              resolve(null);
            } else {
              resolve(result);
            }
          };
          reader.readAsDataURL(formData.businessLogo);
        });
        
        if (!logoBase64) {
          return; // Stop if image is too large
        }
      }

      const payload = {
        businessName: formData.businessName,
        businessEmail: formData.businessEmail,
        businessPhone: formData.businessPhone || undefined,
        EmergencyMobNo: formData.EmergencyMobNo || undefined,
        password: formData.password,
        businessSubscriptionPlan: formData.businessSubscriptionPlan || undefined,
        businessCreatedDate: formData.businessCreatedDate
          ? new Date(formData.businessCreatedDate).toISOString()
          : new Date().toISOString(),
        weeklyHoliday: formData.weeklyHoliday,
        address: formData.address || undefined,
        businessLogo: logoBase64 || undefined,
      };

      console.log("Sending create company request");

      const response = await axios.post(
        'https://customersupport-jhy9.onrender.com/api/companies',
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log("Company created successfully:", response.data);
      fetchCompanies();
      resetForm();
      alert('Company created successfully!');
    } catch (error) {
      console.error("Error creating company:", error.response?.data || error.message || error);
      if (error.response?.status === 413 || error.message.includes('too large')) {
        alert('Request payload too large. Please use a smaller logo image.');
      } else {
        alert('Error creating company. Please try again.');
      }
    }
  };

  const handleEditCompany = (company) => {
    setIsEditing(true);
    setEditingCompanyId(company._id);
    setFormData({
      businessName: company.businessName || '',
      businessEmail: company.businessEmail || '',
      businessPhone: company.businessPhone || '',
      EmergencyMobNo: company.EmergencyMobNo || '',
      password: '', // Password is not pre-filled for security
      businessSubscriptionPlan: company.businessSubscriptionPlan || (subscriptionPlans.length > 0 ? (subscriptionPlans[0].title || subscriptionPlans[0].name || subscriptionPlans[0].planName) : ''),
      businessCreatedDate: company.businessCreatedDate
        ? new Date(company.businessCreatedDate).toISOString().split('T')[0]
        : '',
      weeklyHoliday: company.weeklyHoliday || [],
      address: company.address || '',
      businessLogo: null, // File will be handled separately
    });
    
    // Set logo preview if company has existing logo
    if (company.businessLogo) {
      setLogoPreview(company.businessLogo);
    } else {
      setLogoPreview(null);
    }
    
    setShowForm(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Access token not found. Please login.');
      return;
    }

    try {
      // If there's a new logo file, convert it to base64
      let logoBase64 = null;
      if (formData.businessLogo) {
        const reader = new FileReader();
        logoBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            const result = reader.result;
            // Check if base64 string is too large (limit to ~500KB after encoding)
            if (result.length > 700000) {
              alert('Image is still too large after compression. Please choose a smaller image.');
              resolve(null);
            } else {
              resolve(result);
            }
          };
          reader.readAsDataURL(formData.businessLogo);
        });
        
        if (logoBase64 === null && formData.businessLogo) {
          return; // Stop if image is too large
        }
      }

      const payload = {
        businessName: formData.businessName,
        businessEmail: formData.businessEmail,
        businessPhone: formData.businessPhone || undefined,
        EmergencyMobNo: formData.EmergencyMobNo || undefined,
        password: formData.password || undefined,
        businessSubscriptionPlan: formData.businessSubscriptionPlan || undefined,
        businessCreatedDate: formData.businessCreatedDate
          ? new Date(formData.businessCreatedDate).toISOString()
          : undefined,
        weeklyHoliday: formData.weeklyHoliday,
        address: formData.address || undefined,
        businessLogo: logoBase64 || undefined,
      };

      console.log("Sending update company request");

      const response = await axios.put(
        `https://customersupport-jhy9.onrender.com/api/companies/${editingCompanyId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log("Company updated successfully:", response.data);
      fetchCompanies();
      resetForm();
      alert('Company updated successfully!');
    } catch (error) {
      console.error("Error updating company:", error.response?.data || error.message || error);
      if (error.response?.status === 413 || error.message.includes('too large')) {
        alert('Request payload too large. Please use a smaller logo image.');
      } else {
        alert('Error updating company. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      businessName: '',
      businessEmail: '',
      businessPhone: '',
      EmergencyMobNo: '',
      password: '',
      businessSubscriptionPlan: subscriptionPlans.length > 0 ? (subscriptionPlans[0].title || subscriptionPlans[0].name || subscriptionPlans[0].planName) : '',
      businessCreatedDate: '',
      weeklyHoliday: [],
      address: '',
      businessLogo: null,
    });
    setLogoPreview(null);
    setShowForm(false);
    setIsEditing(false);
    setEditingCompanyId(null);
  };

  const handleViewCompany = (company) => {
    setSelectedCompany(company);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedCompany(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <div className="text-red-500 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Company Management</h1>
            <p className="text-gray-600 mt-2">Manage all companies in your system</p>
          </div>
          <button
            onClick={() => {
              if (showForm) resetForm();
              else setShowForm(true);
            }}
            className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-md hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            {showForm ? 'Cancel' : 'Add Company'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">{isEditing ? 'Edit Company' : 'Add New Company'}</h2>
            <form onSubmit={isEditing ? handleUpdateCompany : handleAddCompany} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter business name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Email *</label>
                  <input
                    type="email"
                    name="businessEmail"
                    value={formData.businessEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="business@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Phone</label>
                  <input
                    type="text"
                    name="businessPhone"
                    value={formData.businessPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Mobile</label>
                  <input
                    type="text"
                    name="EmergencyMobNo"
                    value={formData.EmergencyMobNo}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Emergency contact number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password {isEditing ? '' : '*'}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={isEditing ? 'Enter new password (optional)' : 'Set a password'}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subscription Plan
                    {loadingSubscriptionPlans && (
                      <span className="ml-2 text-xs text-blue-600">
                        <svg className="inline w-3 h-3 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </span>
                    )}
                  </label>
                  <select
                    name="businessSubscriptionPlan"
                    value={formData.businessSubscriptionPlan}
                    onChange={handleInputChange}
                    disabled={loadingSubscriptionPlans}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a subscription plan</option>
                    {loadingSubscriptionPlans ? (
                      <option value="">Loading subscription plans...</option>
                    ) : subscriptionPlansError ? (
                      <>
                        <option value="Basic">Basic</option>
                        <option value="Standard">Standard</option>
                        <option value="Premium">Premium</option>
                        <option value="Enterprise">Enterprise</option>
                      </>
                    ) : subscriptionPlans.length === 0 ? (
                      <>
                        <option value="Basic">Basic</option>
                        <option value="Standard">Standard</option>
                        <option value="Premium">Premium</option>
                        <option value="Enterprise">Enterprise</option>
                      </>
                    ) : (
                      subscriptionPlans.map(plan => {
                        const planTitle = plan.title || plan.name || plan.planName || 'Unknown Plan';
                        const planPrice = plan.price || plan.cost || plan.amount;
                        const planCurrency = plan.currency || '₹';
                        const planDuration = plan.duration || plan.period || plan.billingCycle;
                        
                        // Create display text with proper formatting
                        let displayText = planTitle;
                        if (planPrice) {
                          displayText += ` - ${planCurrency}${planPrice}`;
                        }
                        if (planDuration) {
                          displayText += ` (${planDuration})`;
                        }
                        
                        return (
                          <option key={plan._id || plan.id} value={planTitle}>
                            {displayText}
                          </option>
                        );
                      })
                    )}
                  </select>
                  {subscriptionPlansError && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {subscriptionPlansError}
                    </p>
                  )}
                  {!loadingSubscriptionPlans && !subscriptionPlansError && subscriptionPlans.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-green-600 flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {subscriptionPlans.length} subscription plans available
                      </p>
                      {formData.businessSubscriptionPlan && (() => {
                        const selectedPlan = subscriptionPlans.find(plan => 
                          (plan.title || plan.name || plan.planName) === formData.businessSubscriptionPlan
                        );
                        if (selectedPlan) {
                          return (
                            <div className="mt-1 p-2 bg-blue-50 rounded-md border border-blue-200">
                              <p className="text-xs text-blue-800 font-medium">
                                📋 Selected Plan Details:
                              </p>
                              <div className="text-xs text-blue-700 mt-1 space-y-1">
                                <div><strong>Title:</strong> {selectedPlan.title || selectedPlan.name || selectedPlan.planName}</div>
                                {(selectedPlan.price || selectedPlan.cost || selectedPlan.amount) && (
                                  <div><strong>Price:</strong> {selectedPlan.currency || '₹'}{selectedPlan.price || selectedPlan.cost || selectedPlan.amount}</div>
                                )}
                                {(selectedPlan.duration || selectedPlan.period || selectedPlan.billingCycle) && (
                                  <div><strong>Duration:</strong> {selectedPlan.duration || selectedPlan.period || selectedPlan.billingCycle}</div>
                                )}
                                {selectedPlan.description && (
                                  <div><strong>Description:</strong> {selectedPlan.description}</div>
                                )}
                                {selectedPlan.features && selectedPlan.features.length > 0 && (
                                  <div><strong>Features:</strong> {selectedPlan.features.length} features included</div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                  <input
                    type="date"
                    name="businessCreatedDate"
                    value={formData.businessCreatedDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Weekly Holidays</label>
                  <div className="grid grid-cols-2 gap-2">
                    {daysOfWeek.map(day => (
                      <label key={day} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.weeklyHoliday.includes(day)}
                          onChange={() => handleWeeklyHolidayChange(day)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Company address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo</label>
                  <input
                    type="file"
                    name="businessLogo"
                    onChange={handleInputChange}
                    accept="image/*"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  />
                  {logoPreview && (
                    <div className="mt-2">
                      <img
                        src={logoPreview}
                        alt="Logo Preview"
                        className="h-20 w-20 object-cover rounded-md border border-gray-300"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="md:col-span-2 pt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  {isEditing ? 'Update Company' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Company List</h2>
            <p className="text-gray-600 text-sm">{companies.length} companies found</p>
          </div>

          {companies.length === 0 ? (
            <div className="p-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m2-0h-2m0-3h.01M17 18h.01M14 18h.01" />
              </svg>
              <p className="mt-4 text-gray-600">No companies found. Add your first company to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logo</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Phone</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weekly Holidays</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Plan</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company, index) => (
                    <tr key={company._id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.businessLogo ? (
                          <img
                            src={company.businessLogo}
                            alt="Company Logo"
                            className="h-10 w-10 object-cover rounded-full border border-gray-200"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0h-2m0-3h.01M17 18h.01M14 18h.01" />
                            </svg>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{company.businessName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{company.businessEmail}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{company.businessPhone || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company.weeklyHoliday && company.weeklyHoliday.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {company.weeklyHoliday.map((day, idx) => (
                              <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                {day.substring(0, 3)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          'None'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company.businessCreatedDate ? new Date(company.businessCreatedDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          company.businessSubscriptionPlan 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {company.businessSubscriptionPlan || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => handleViewCompany(company)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        <button 
                          onClick={() => handleEditCompany(company)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button className="text-red-600 hover:text-red-900">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* View Company Modal */}
        {showViewModal && selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-800">Company Details</h2>
                <button
                  onClick={closeViewModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Company Logo Section */}
                  <div className="lg:col-span-1">
                    <div className="text-center">
                      <div className="mb-4">
                        {selectedCompany.businessLogo ? (
                          <img
                            src={selectedCompany.businessLogo}
                            alt="Company Logo"
                            className="w-32 h-32 object-cover rounded-full mx-auto border-4 border-gray-200 shadow-lg"
                          />
                        ) : (
                          <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mx-auto flex items-center justify-center border-4 border-gray-200 shadow-lg">
                            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0h-2m0-3h.01M17 18h.01M14 18h.01" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{selectedCompany.businessName}</h3>
                      <p className="text-gray-600">{selectedCompany.businessEmail}</p>
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Basic Information */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Basic Information</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Business Name</label>
                          <p className="text-gray-800 font-medium">{selectedCompany.businessName}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Business Email</label>
                          <p className="text-gray-800">{selectedCompany.businessEmail}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Business Phone</label>
                          <p className="text-gray-800">{selectedCompany.businessPhone || 'Not provided'}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Emergency Mobile</label>
                          <p className="text-gray-800">{selectedCompany.EmergencyMobNo || 'Not provided'}</p>
                        </div>
                      </div>

                      {/* Additional Information */}
                      <div className="space-y-4">
                        <h4 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Additional Information</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Subscription Plan</label>
                          <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                            selectedCompany.businessSubscriptionPlan 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedCompany.businessSubscriptionPlan || 'Not specified'}
                          </span>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
                          <p className="text-gray-800">
                            {selectedCompany.businessCreatedDate 
                              ? new Date(selectedCompany.businessCreatedDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                              : 'Not specified'
                            }
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Weekly Holidays</label>
                          <div className="flex flex-wrap gap-2">
                            {selectedCompany.weeklyHoliday && selectedCompany.weeklyHoliday.length > 0 ? (
                              selectedCompany.weeklyHoliday.map((day, idx) => (
                                <span key={idx} className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                                  {day}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-500">No holidays specified</span>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">Address</label>
                          <p className="text-gray-800">{selectedCompany.address || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Company ID */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Company ID</label>
                        <p className="text-gray-600 font-mono text-sm bg-gray-50 px-3 py-2 rounded-md">{selectedCompany._id}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => {
                    closeViewModal();
                    handleEditCompany(selectedCompany);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Edit Company
                </button>
                <button
                  onClick={closeViewModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Companies;